import { useQuery } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { useMe } from '../../hooks/useMe'
import { C, F } from './theme'
import { Card, SectionHead, Pill, DeptChip, Avatar, Icon, gbp } from './prims'
import { ForecastFinanceView } from './ForecastFinanceView'

// EM-3 · Event → Forecast tab. A READ-ONLY summary of the forecast that produced
// this event, surfaced on the (committed) Draft event. The forecast data stays
// attached to the same event_id through the Idea→Draft promotion, so nothing is
// copied — we simply read it back and render it read-only. Finance is shown to
// financial users (ForecastFinanceView); the plan detail (stakeholders,
// sign-offs, change notice, red flags, business plan) is admin-only because the
// underlying reads are admin-gated in the Forecast Studio API.

const SIGN_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: C.amberSoft, fg: C.amber, label: 'Pending' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  rejected: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Rejected' }
}
const APPR_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: C.amberSoft, fg: C.amber, label: 'Pending' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  blocked: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Blocked' }
}
const RESP_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  confirmed: { bg: C.greenBg, fg: C.green, label: 'Confirmed' },
  commented: { bg: C.slateSoft, fg: C.slate, label: 'Commented' },
  'red-alert': { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Red alert' }
}
const SEV_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  watch: { bg: C.amberSoft, fg: C.amber, label: 'Watch' },
  high: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'High' }
}

function chip(s: { bg: string; fg: string; label: string }) {
  return <span style={{ padding: '2px 9px', borderRadius: 999, background: s.bg, color: s.fg, fontFamily: F.body, fontSize: 11, fontWeight: 800 }}>{s.label}</span>
}

export function EventForecastTab({ eventId }: { eventId: number }) {
  const { data: me } = useMe()
  const admin = !!me?.isAdmin

  const fcQ = useQuery({ queryKey: ['forecast-ro', eventId], queryFn: () => ApiClient.forecast(eventId), enabled: admin, retry: false })
  const cnQ = useQuery({ queryKey: ['change-notice', eventId], queryFn: async () => (await ApiClient.changeNotice(eventId)).notice, enabled: admin, retry: false })
  const docsQ = useQuery({ queryKey: ['forecast-plan-docs-ro', eventId], queryFn: async () => (await ApiClient.forecastPlanDocs(eventId)).docs, enabled: admin, retry: false })

  const fc = fcQ.data ?? null
  const notice = cnQ.data ?? null
  const docs = docsQ.data ?? []
  const redFlagContracts = (fc?.contracts ?? []).filter((c) => c.redFlagSeverity && c.redFlagSeverity !== 'none')
  const blockers = notice?.blockers ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: F.body }}>
      {/* Finance mirror (financial-gated inside) */}
      <ForecastFinanceView eventId={eventId} />

      {!admin && (
        <Card>
          <Pill fg={C.slate} bg={C.slateSoft} icon="lock">The forecast plan detail (stakeholders, sign-offs and change notice) is visible to admins.</Pill>
        </Card>
      )}

      {admin && (
        <>
          {/* Stakeholders / approval team */}
          <Card>
            <SectionHead icon="users" label="Stakeholders & approvals" right={<span style={{ fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint }}>read-only</span>} />
            {(fc?.team ?? []).length === 0 ? (
              <div style={{ fontSize: 13, color: C.inkFaint }}>No delivery functions were assigned.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(fc?.team ?? []).map((t) => {
                  const rs = t.responseStatus ? RESP_STYLE[t.responseStatus] : null
                  return (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', border: `1px solid ${C.lineSoft}`, borderRadius: 10, background: C.paper }}>
                      <Avatar name={t.personName} size={30} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: F.serif, fontSize: 14, fontWeight: 700, color: C.ink }}>{t.personName}</span>
                          <DeptChip id={t.dept} small />
                          {t.kind === 'exec-signoff' && <Pill fg={C.brassDk} bg={C.brassSoft} icon="star">Exec sign-off</Pill>}
                        </div>
                        <div style={{ fontSize: 12, color: C.inkSoft }}>{t.functionTitle}</div>
                      </div>
                      {rs && chip(rs)}
                      {chip(APPR_STYLE[t.approvalStatus] ?? APPR_STYLE.pending!)}
                    </div>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Change notice */}
          {notice && (
            <Card>
              <SectionHead icon="file" label="Change notice" right={notice.status ? chip(SIGN_STYLE[notice.status] ?? { bg: C.slateSoft, fg: C.slate, label: notice.status }) : undefined} />
              <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 800, color: C.ink, marginBottom: 6 }}>{notice.title}</div>
              {notice.whatChanges && <p style={{ fontSize: 13.5, color: C.inkSoft, margin: '0 0 10px' }}>{notice.whatChanges}</p>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {notice.affectedDepts.map((d) => <DeptChip key={d} id={d} small />)}
                {notice.goLive && <Pill fg={C.slate} bg={C.slateSoft} icon="calendar">Go-live {notice.goLive}</Pill>}
                {notice.reqs.documentation && <Pill fg={C.inkSoft} bg={C.paper}>Docs</Pill>}
                {notice.reqs.process && <Pill fg={C.inkSoft} bg={C.paper}>Process</Pill>}
                {notice.reqs.training && <Pill fg={C.inkSoft} bg={C.paper}>Training</Pill>}
                {notice.reqs.comms && <Pill fg={C.inkSoft} bg={C.paper}>Comms</Pill>}
              </div>

              {/* Sign-offs */}
              {notice.signoff.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {notice.signoff.map((s) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, flex: 1 }}>{s.role}{s.name && s.name !== s.role ? ` · ${s.name}` : ''}</span>
                      {chip(SIGN_STYLE[s.status] ?? SIGN_STYLE.pending!)}
                    </div>
                  ))}
                </div>
              )}

              {/* Activity feed — comments & red alerts */}
              {notice.feed.length > 0 && (
                <div style={{ borderTop: `1px solid ${C.lineSoft}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.inkFaint }}>Comments & red alerts</div>
                  {notice.feed.map((f) => (
                    <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <Icon name={f.kind === 'alert' ? 'alert' : 'msg'} size={13} color={f.kind === 'alert' ? C.oxblood : C.slate} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: C.ink }}>{f.body}</div>
                        <div style={{ fontSize: 11, color: C.inkFaint }}>{f.authorName ?? 'Someone'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Red flags — CN blockers + contract red flags */}
          {(blockers.length > 0 || redFlagContracts.length > 0) && (
            <Card style={{ borderColor: C.oxblood, background: C.oxbloodSoft }}>
              <SectionHead icon="flag" label="Red flags" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {blockers.map((b, i) => (
                  <div key={`b${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Icon name="alert" size={13} color={C.oxblood} />
                    <span style={{ fontSize: 13, color: C.ink }}>{b}</span>
                  </div>
                ))}
                {redFlagContracts.map((c) => (
                  <div key={`c${c.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Icon name="alert" size={13} color={C.oxblood} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{c.companyName}</span>
                    {c.redFlagSeverity && SEV_STYLE[c.redFlagSeverity] && chip(SEV_STYLE[c.redFlagSeverity]!)}
                    {c.redFlagNote && <span style={{ fontSize: 12.5, color: C.inkSoft }}>{c.redFlagNote}</span>}
                    <span style={{ fontFamily: F.mono, fontSize: 11, color: C.inkFaint }}>fee {gbp(c.feePence)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Business plan versions */}
          {docs.length > 0 && (
            <Card>
              <SectionHead icon="book" label="Business plan" right={<span style={{ fontFamily: F.mono, fontSize: 10.5, color: C.inkFaint }}>{docs.length} version{docs.length === 1 ? '' : 's'}</span>} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {docs.map((d) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', border: `1px solid ${C.lineSoft}`, borderRadius: 9 }}>
                    <Pill fg={d.source === 'claude' ? C.green : C.slate} bg={d.source === 'claude' ? C.greenBg : C.slateSoft}>{d.source === 'claude' ? 'Composed' : 'Uploaded'}</Pill>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>v{d.version}</span>
                    <span style={{ fontSize: 12.5, color: C.inkSoft, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</span>
                    <a href={`/api/forecast/plan-docs/${d.id}/download`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none', color: C.brassDk, fontFamily: F.body, fontSize: 12, fontWeight: 700 }}>
                      <Icon name="download" size={13} />Download
                    </a>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
